/* eslint-disable filenames/match-regex */

import { RULE_ACTION } from '@/consts.js';
import RuleService from '@/services/Rule.js';

const ITEM_PER_PAGE = 20;
const POLL_INTERVAL_MS = 5000;

// Module-level polling registry. Kept outside Vuex state because timer handles
// and observer Sets are not reactive concerns.
const pollingRegistry = {
  observed: {},
  timer: null,
  inFlight: false,
};

function ruleNeedsPolling(entry) {
  if (!entry || !entry.rule) {
    return false;
  }

  if (entry.forcePollsRemaining > 0) {
    return true;
  }

  const { rule } = entry;

  if (rule.is_running) {
    return true;
  }

  if (!rule.active) {
    return false;
  }

  if (rule.last_run && rule.mins_between_runs > 0) {
    const interval = rule.mins_between_runs * 60000;
    const elapsed = Date.now() - new Date(rule.last_run).getTime();
    if (elapsed >= interval) {
      return true;
    }
  }

  if (rule.next_run) {
    return new Date(rule.next_run).getTime() <= Date.now();
  }

  return false;
}

function ensurePollingTimer(dispatch) {
  if (!pollingRegistry.timer) {
    pollingRegistry.timer = setInterval(
      () => dispatch('pollObservedRules'),
      POLL_INTERVAL_MS,
    );
  }
}

function maybeStopPollingTimer() {
  if (Object.keys(pollingRegistry.observed).length === 0 && pollingRegistry.timer) {
    clearInterval(pollingRegistry.timer);
    pollingRegistry.timer = null;
  }
}

const state = {
  isLoading: false,
  hasMore: true,
  page: 0,
  actionFilter: [ RULE_ACTION.START, RULE_ACTION.STOP, RULE_ACTION.SCALING_UP ],
  brandId: null,
  statusFilter: null,
  searchTerm: '',
  orderBy: 'created_at',
  order: 'desc',
  list: [],
  hasAnyRules: false,
  analytics: [],
};

const getters = {
  isLoading(state) {
    return state.isLoading;
  },
  hasMore(state) {
    return state.hasMore;
  },
  page(state) {
    return state.page;
  },
  actionFilter(state) {
    return state.actionFilter || [];
  },
  actionFilterIsEmpty(state) {
    return !state.actionFilter || state.actionFilter.length === 0 || state.actionFilter.length === Object.keys(RULE_ACTION).length;
  },
  brandId(state) {
    return state.brandId;
  },
  statusFilter(state) {
    return state.statusFilter;
  },
  searchTerm(state) {
    return state.searchTerm;
  },
  orderBy(state) {
    return state.orderBy;
  },
  order(state) {
    return state.order;
  },
  list(state) {
    return state.list;
  },
  hasAnyRules(state) {
    return state.hasAnyRules;
  },
  analytics(state) {
    return state.analytics;
  },
  ruleAnalytics(state) {
    return function(ruleId) {
      return state.analytics.find(item => item.rule_id === ruleId) || null;
    };
  },
};

const actions = {
  resetView(store, keepTerm) {
    store.commit('setIsLoading', false);
    store.commit('setHasMore', true);
    store.commit('setPage', 0);
    store.commit('setList', []);
    store.commit('setAnalytics', []);

    if (!keepTerm) {
      store.commit('resetHasAnyRulesValue');
      store.commit('setActionFilter', [ RULE_ACTION.START, RULE_ACTION.STOP, RULE_ACTION.SCALING_UP ]);
      store.commit('setBrandId', null);
      store.commit('setStatusFilter', null);
      store.commit('setSearchTerm', '');
    }
  },
  loadMoreData(store) {
    if (
      store.getters['isLoading']
      || (!store.getters['hasMore'] && store.getters['page'] > 1)
    ) {
      return [];
    }

    store.commit('setIsLoading', true);
    store.commit('increasePage');

    const params = {
      term: store.getters['searchTerm'],
      actions: store.getters['actionFilter'],
      brandId: store.getters['brandId'],
      campaignStatus: store.getters['statusFilter'],
      orderBy: store.getters['orderBy'],
      order: store.getters['order'],
      page: store.getters['page'],
      limit: ITEM_PER_PAGE,
    };

    if (store.getters['statusFilter'] !== null) {
      params['status'] = store.getters['statusFilter'] ? 1 : 0;
    }

    return RuleService.getList(params)
      .then(resp => {
        const list = store.getters['list'];
        resp.forEach(item => list.push(item));
        store.commit('setList', list);
        store.commit('setHasMore', resp.length === ITEM_PER_PAGE);

        if (resp.length > 0) {
          const rulesWithOutputCampaign = resp.filter(rule => rule.has_output_campaign);

          if (rulesWithOutputCampaign.length !== 0) {
            const ruleIds = rulesWithOutputCampaign.map(rule => rule.id);
            return RuleService.getRuleAnalyticsForChart(ruleIds)
              .then(analytics => {
                store.commit('addAnalytics', analytics);
                return resp;
              });
          }
        }

        return resp;
      })
      .finally(() => {
        store.commit('setIsLoading', false);
      })
      .catch(err => {
        store.commit('setHasMore', false);
        throw err;
      });
  },

  observeRule({ dispatch }, { rule, observerId, options = {} }) {
    if (!rule || !rule.id || observerId === undefined || observerId === null) {
      return;
    }

    let entry = pollingRegistry.observed[rule.id];
    if (!entry) {
      entry = {
        rule,
        forcePollsRemaining: 0,
        observers: new Set(),
      };
      pollingRegistry.observed[rule.id] = entry;
    }
    else {
      entry.rule = rule;
    }

    entry.observers.add(observerId);
    entry.forcePollsRemaining = Math.max(
      entry.forcePollsRemaining,
      options.minAttempts || 0,
    );

    ensurePollingTimer(dispatch);

    if (entry.forcePollsRemaining > 0 || ruleNeedsPolling(entry)) {
      dispatch('pollObservedRules');
    }
  },

  unobserveRule(_, { ruleId, observerId }) {
    const entry = pollingRegistry.observed[ruleId];
    if (!entry) {
      return;
    }
    entry.observers.delete(observerId);
    if (entry.observers.size === 0) {
      delete pollingRegistry.observed[ruleId];
    }
    maybeStopPollingTimer();
  },

  unobserveAllRulesByObserver(_, observerId) {
    for (const ruleId of Object.keys(pollingRegistry.observed)) {
      const entry = pollingRegistry.observed[ruleId];
      entry.observers.delete(observerId);
      if (entry.observers.size === 0) {
        delete pollingRegistry.observed[ruleId];
      }
    }
    maybeStopPollingTimer();
  },

  pollObservedRules({ commit }) {
    if (pollingRegistry.inFlight) {
      return Promise.resolve();
    }

    const entries = Object.values(pollingRegistry.observed).filter(ruleNeedsPolling);
    if (entries.length === 0) {
      return Promise.resolve();
    }

    entries.forEach(entry => {
      if (entry.forcePollsRemaining > 0) {
        entry.forcePollsRemaining -= 1;
      }
    });

    const ids = entries.map(entry => entry.rule.id);
    pollingRegistry.inFlight = true;

    return RuleService.getStatus(ids)
      .then(updatedList => {
        if (!Array.isArray(updatedList)) {
          return;
        }

        for (const updated of updatedList) {
          if (!updated || !updated.id) {
            continue;
          }

          const entry = pollingRegistry.observed[updated.id];
          if (entry) {
            entry.rule.updateWith(updated);
          }
          commit('updateInList', updated);
        }

        return;
      })
      .catch(() => {
        // Best effort; let next tick retry.
      })
      .finally(() => {
        pollingRegistry.inFlight = false;
      });
  },
};

const mutations = {
  setIsLoading(state, value) {
    state.isLoading = value;
  },
  setHasMore(state, value) {
    state.hasMore = value;
  },
  setPage(state, value) {
    state.page = value;
  },
  increasePage(state) {
    state.page += 1;
  },
  setActionFilter(state, value) {
    if (value === null) {
      value = [ RULE_ACTION.START, RULE_ACTION.STOP, RULE_ACTION.SCALING_UP ];
    }

    state.actionFilter = Array.isArray(value) ? value : [ value ];
  },
  setBrandId(state, value) {
    state.brandId = value;
  },
  setStatusFilter(state, value) {
    state.statusFilter = value;
  },
  setSearchTerm(state, value) {
    state.searchTerm = value;
  },
  setOrderBy(state, value) {
    state.orderBy = value;
  },
  setOrder(state, value) {
    state.order = value;
  },
  setVisibleFields(state, value) {
    state.visibleFields = value;
  },
  setList(state, value) {
    state.list = value;
    if (value.length > 0) {
      state.hasAnyRules = true;
    }
  },
  resetHasAnyRulesValue(state) {
    state.hasAnyRules = false;
  },
  ruleRemoved(state, id) {
    state.list = state.list.filter(item => item.id !== id);
  },
  updateInList(state, entity) {
    const entityInList = state.list.find(item => item.id === entity.id);
    if (entityInList) {
      entityInList.updateWith(entity);
    }
  },
  setAnalytics(state, analytics) {
    state.analytics = analytics;
  },
  addAnalytics(state, analytics) {
    state.analytics = [ ...state.analytics, ...analytics ];
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
